import { ErrorMessage, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import AuthShell from '../../app/common/ui/AuthShell'
import { AppButton, AppInput, AppPasswordInput, AppStack, AppText } from '../../app/common/ui'

function friendlyValidationMessage (field: string, message: string): string {
  const key = field.toLowerCase()
  if (key === 'password' && /match the regular expression|regex/.test(message)) { return 'Password must be 6–100 characters and include at least one number, one lowercase letter, and one uppercase letter.' }
  if (key === 'username' && /already taken|duplicate|taken/i.test(message)) { return 'That username is already taken.' }
  if (key === 'email' && /already taken|duplicate|taken/i.test(message)) { return 'Email taken' }
  return message.replace(/^The field \w+ /, '').trim() || message
}

function formatRegistrationError (error: any): string {
  const data = error?.response?.data
  if (!data) return error?.message || 'Registration failed.'
  if (data.detail && typeof data.detail === 'string') return data.detail
  const errs = data.errors
  if (errs && typeof errs === 'object') {
    const lines: string[] = []
    for (const key of Object.keys(errs)) {
      const messages = Array.isArray(errs[key]) ? errs[key] : [errs[key]]
      const friendly = messages.map((m: string) => friendlyValidationMessage(key, m))
      lines.push(...friendly)
    }
    if (lines.length) return lines.join(' ')
  }
  return 'Registration failed. Please check your details and try again.'
}

export default observer(function RegisterForm () {
  const { userStore } = useStore()

  return (
    <AuthShell title="CREATE ACCOUNT">
      <Formik
        initialValues={{ displayName: '', username: '', email: '', password: '', error: '' }}
        onSubmit={async (values, { setErrors }) => {
          try {
            await userStore.register({
              displayName: values.displayName,
              username: values.username,
              email: values.email,
              password: values.password
            })
          } catch (error: any) {
            const friendly = formatRegistrationError(error)
            setErrors({ error: friendly })
            if (friendly) toast.error(friendly)
          }
        }}
        validationSchema={Yup.object({
          displayName: Yup.string().required('Display name is required'),
          username: Yup.string().required('Username is required'),
          email: Yup.string().required('Email is required').email('Invalid email'),
          password: Yup.string().required('Password is required')
        })}
      >
        {({ handleSubmit, isSubmitting, errors, isValid, dirty, values, handleChange, handleBlur, touched }) => (
          <form onSubmit={handleSubmit} autoComplete="off">
            <AppStack spacing="sm">
              <AppInput
                name="displayName"
                placeholder="Display Name"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.displayName && (errors as any).displayName}
              />
              <AppInput
                name="username"
                placeholder="Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.username && (errors as any).username}
              />
              <AppInput
                name="email"
                placeholder="Email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && (errors as any).email}
              />
              <AppPasswordInput
                name="password"
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && (errors as any).password}
                description="6–100 characters, with at least one number, one lowercase letter, and one uppercase letter"
              />
              <ErrorMessage
                name="error"
                render={() => (
                  <AppText size="sm" color="red" mb="xs">
                    {errors.error}
                  </AppText>
                )}
              />
              <AppButton
                type="submit"
                disabled={!isValid || !dirty || isSubmitting}
                loading={isSubmitting}
                fullWidth
                size="md"
              >
                Create Account
              </AppButton>
            </AppStack>
          </form>
        )}
      </Formik>

      <AppText size="xs" ta="center" mt="lg" sx={{ color: 'var(--gs-muted)' }}>
        Already have an account?{' '}
        <AppText component={Link} to="/login" sx={{ color: '#58A6FF' }}>
          Sign in →
        </AppText>
      </AppText>
    </AuthShell>
  )
})
